import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Loader2, Plus, Settings2, Trash2 } from "lucide-react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import type { AccountDTO } from "@app/shared";
import { api, brandLogoUrl } from "../lib/api";
import { useBrands } from "../lib/brand";
import { Button, buttonVariants } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../components/Toast";

function msg(e: unknown): string {
  return e instanceof Error ? e.message : "Terjadi kesalahan";
}

/** Derive a valid brand slug from a display name (lowercase, hyphenated, starts with a letter). */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^[^a-z]+/, "")
    .replace(/-+$/g, "");
}

/**
 * Admin "Kelola Brand" LIST page. Add or remove brands here; click a brand to
 * open its full configuration (`/brands/:key`). Admin-only.
 */
export function Brands() {
  const { isAdmin } = useBrands();
  const qc = useQueryClient();
  const toast = useToast();

  const brandsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: ({ signal }) => api.accounts(signal),
  });
  const brands = brandsQuery.data ?? [];

  const [displayName, setDisplayName] = useState("");
  const [key, setKey] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [accentColor, setAccentColor] = useState("#33BEBA");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api.createBrand({
        key: key.trim(),
        displayName: displayName.trim(),
        accentColor,
        // Brand + its first login are created together, atomically.
        login: { email: email.trim(), password },
      }),
    onSuccess: () => {
      toast.success(`Brand "${key.trim()}" + login dibuat`);
      setDisplayName("");
      setKey("");
      setSlugEdited(false);
      setEmail("");
      setPassword("");
      void qc.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (e) => toast.error(msg(e)),
  });

  // Typing the name auto-fills the slug until the admin edits the slug directly.
  const onNameChange = (v: string) => {
    setDisplayName(v);
    if (!slugEdited) setKey(slugify(v));
  };

  const canCreate =
    /^[a-z][a-z0-9-]{1,31}$/.test(key.trim()) &&
    displayName.trim().length > 0 &&
    email.includes("@") &&
    password.length >= 8 &&
    !create.isPending;

  // Brand management is admin-only (nav hides it; this guards direct URLs).
  if (!isAdmin) return <Navigate to="/drafts" replace />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-navy">Brand</h1>
        <p className="text-sm text-slate-500">
          Daftar brand. Tambah atau hapus di sini, lalu klik "Konfigurasi" untuk
          mengatur personalisasi tiap brand.
        </p>
      </div>

      {/* Create brand */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tambah brand baru</CardTitle>
          <CardDescription>
            Brand dibuat sekaligus dengan login-nya. Slug terisi otomatis dari
            nama (bisa diedit) dan bersifat permanen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_12rem_7rem]">
            <div className="space-y-1.5">
              <Label htmlFor="new-name">Nama tampilan</Label>
              <Input
                id="new-name"
                placeholder="mis. Acme"
                value={displayName}
                onChange={(e) => onNameChange(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-key">Slug</Label>
              <Input
                id="new-key"
                placeholder="acme"
                value={key}
                onChange={(e) => {
                  setKey(e.target.value.toLowerCase());
                  setSlugEdited(true);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-color">Warna aksen</Label>
              <div className="flex items-center gap-2">
                <input
                  id="new-color"
                  type="color"
                  value={
                    /^#[0-9a-fA-F]{6}$/.test(accentColor)
                      ? accentColor
                      : "#33BEBA"
                  }
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-slate-300 bg-white p-1"
                  aria-label="Pilih warna"
                />
                <Input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  placeholder="#33BEBA"
                  maxLength={7}
                  className="flex-1 font-mono"
                  aria-label="Kode hex warna"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="new-email">Email login (brand)</Label>
              <Input
                id="new-email"
                type="email"
                autoComplete="off"
                placeholder="owner@brand.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-pass">Password (min 8)</Label>
              <Input
                id="new-pass"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <Button
            variant="primary"
            onClick={() => create.mutate()}
            disabled={!canCreate}
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Tambah brand + login
          </Button>
          <p className="text-xs text-slate-500">
            Login ini hanya bisa mengelola konten brand tersebut. IG/WordPress, AI
            guide, logo, dan login tambahan diatur lewat "Konfigurasi".
          </p>
        </CardContent>
      </Card>

      {/* Brand list */}
      {brandsQuery.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-teal" />
        </div>
      ) : brands.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-500">
          Belum ada brand. Tambahkan satu di atas.
        </p>
      ) : (
        <div className="space-y-3">
          {brands.map((b) => (
            <BrandRow key={b.key} brand={b} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One brand row in the list: identity + connection badges + actions. */
function BrandRow({ brand }: { brand: AccountDTO }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { setActiveKey } = useBrands();

  const remove = useMutation({
    mutationFn: () => api.deleteBrand(brand.key),
    onSuccess: () => {
      toast.success(`Brand "${brand.key}" dihapus`);
      void qc.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (e) => toast.error(msg(e)),
  });

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-white p-4">
      {brand.hasLogo ? (
        <img
          src={brandLogoUrl(brand.key)}
          alt=""
          className="h-11 w-11 shrink-0 rounded-md border border-slate-200 bg-navy object-contain p-1"
        />
      ) : (
        <span
          className="h-11 w-11 shrink-0 rounded-md ring-1 ring-slate-300"
          style={{ backgroundColor: brand.accentColor ?? "#33BEBA" }}
        />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-navy">{brand.displayName}</span>
          <span className="font-mono text-xs text-slate-400">{brand.key}</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Badge variant={brand.connected ? "green" : "secondary"}>
            {brand.connected ? "Instagram ✓" : "Instagram —"}
          </Badge>
          <Badge variant={brand.wpConnected ? "green" : "secondary"}>
            {brand.wpConnected ? "WordPress ✓" : "WordPress —"}
          </Badge>
        </div>
      </div>

      <Button
        variant="primary"
        onClick={() => {
          setActiveKey(brand.key);
          navigate("/drafts");
        }}
      >
        Buka
        <ArrowRight className="h-4 w-4" />
      </Button>
      <Link
        to={`/brands/${brand.key}`}
        className={buttonVariants({ variant: "outline" })}
      >
        <Settings2 className="h-4 w-4" />
        Konfigurasi
      </Link>
      <Button
        variant="ghost"
        className="text-red-600 hover:bg-red-50 hover:text-red-700"
        onClick={() => {
          if (
            window.confirm(
              `Hapus brand "${brand.key}"? Hanya bisa jika brand tidak punya konten.`,
            )
          ) {
            remove.mutate();
          }
        }}
        disabled={remove.isPending}
      >
        {remove.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Trash2 className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
}
